import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import { useMfa } from "hooks/useMfa";
import { useSubscription } from "hooks/useSubscription";
import { useFirebaseToken } from "hooks/useFirebaseToken";
import { decodeId } from "utils";
import Helper from "utils/helper";
import { buildAcquireTokenSilentRequest } from "services/authProvider";
import { isSyntheticMsalAccount } from "services/msalSessionRecovery";
import { shouldForceInteractiveReLogin } from "hooks/useMsalAppSession";
import { clearMfaValidated, readMfaValidatedRaw } from "utils/mfaStorage";
import {
  clearB2cAuthStoragePreservingDevice,
  redirectToB2cSignInFromMfa,
  scheduleB2cSignInRedirect,
} from "utils/b2cRedirectErrors";
import { handleWakePhaseAuthFailure } from "utils/handleWakePhaseAuthFailure";
import { isLogoutInProgress } from "utils/appLogout";
import {
  isProtectedDeepLink,
  rememberPostLoginRedirect,
  POST_LOGIN_REDIRECT_KEY,
  POST_LOGIN_USER_ID_KEY,
} from "utils/protectedDeepLink";

/**
 * Returns true if `msal.idtoken` is already present in localStorage (e.g. from
 * a previous session that hasn't expired), so the MFA check can run without
 * waiting for the first acquireTokenSilent round-trip.
 */
function isTokenAlreadyCached() {
  return !!localStorage.getItem("msal.idtoken");
}

export const useAppSharedState = ({
  profile,
  subscriptionStatus,
  getProfile,
  getSubscriptionStatus,
  getCompanyInformation,
  account,
  instance,
  isAuthenticated,
  msalStartupPending = false,
}) => {
  const location = useLocation();
  const history = useHistory();
  const [errorType, setErrorType] = useState(false);
  /**
   * Flips to true once acquireTokenSilent resolves and `msal.idtoken` is stored
   * in localStorage. Initialised to true when a cached token already exists so
   * that returning users don't pay an extra render cycle.
   */
  const [tokenAcquired, setTokenAcquired] = useState(isTokenAlreadyCached);
  const hasAppRedirectedRef = useRef(false);
  const msalRecoveryFallbackRef = useRef(false);
  /** Prevents double getProfile after MFA (isMfaValidate + account churn). */
  const profileBootstrapRef = useRef(false);
  const { signinHandler, signupHandler, logout, isLoginRedirectPending, cancelPendingLoginRedirect } = useAuth();
  const { getTokenManageState } = useFirebaseToken();

  const handleGetTokenManageState = useCallback(async () => {
    await getTokenManageState(getProfile, getCompanyInformation);
  }, [getTokenManageState, getProfile, getCompanyInformation]);

  const {
    isMfaValidate,
    isMfaChecking,
    mfaHomePreflightDone,
    setIsMfaValidate,
  } = useMfa(
    handleGetTokenManageState,
    tokenAcquired,
    isAuthenticated,
  );

  /** When session ends, realign with whatever is left in localStorage (usually none after logout). */
  useEffect(() => {
    if (!isAuthenticated || !account) {
      setTokenAcquired(!!localStorage.getItem("msal.idtoken"));
      // Do not clear the bootstrap guard while MFA + token still look valid —
      // MSAL account churn after verify would otherwise re-run getProfile and
      // remount the post-login landing page (/offices).
      if (!readMfaValidatedRaw() || !Helper.isLoggedIn()) {
        profileBootstrapRef.current = false;
      }
    }
  }, [isAuthenticated, account]);

  /**
   * B2C re-login when persisted session exists but token expired and MSAL cannot recover.
   * Preserves `mfa-validate` — user should not repeat MFA on trusted device.
   */
  useEffect(() => {
    if (account || msalStartupPending) {
      msalRecoveryFallbackRef.current = false;
      return;
    }
    if (!shouldForceInteractiveReLogin(instance) || msalRecoveryFallbackRef.current) {
      return;
    }
    msalRecoveryFallbackRef.current = true;
    signinHandler();
  }, [account, instance, signinHandler, msalStartupPending]);

  const { statusModal, closeModal, shouldShowTrialModal } = useSubscription(
    profile,
    subscriptionStatus,
    getSubscriptionStatus,
  );

  useEffect(() => {
    if (!isAuthenticated || !account || isSyntheticMsalAccount(account)) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await instance.acquireTokenSilent(
          buildAcquireTokenSilentRequest(account),
        );
        if (!cancelled && result?.idToken) {
          localStorage.setItem("msal.idtoken", result.idToken);
          // Signal useMfa that a valid token is now in localStorage so its
          // effect re-evaluates and calls checkMfaStatus with a real token.
          setTokenAcquired(true);
        }
      } catch (e) {
        if (!handleWakePhaseAuthFailure(e, signinHandler)) {
          console.warn("acquireTokenSilent failed:", e);
        }
        // Avoid infinite MFA gate loader if silent acquisition fails but the user
        // is already signed in (e.g. policy / cache edge cases).
        if (!cancelled) {
          setTokenAcquired(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, account, instance, signinHandler]);

  useEffect(() => {
    if (window.location.href.includes("#")) {
      const msalError = localStorage.getItem("msal.error");
      if (msalError === "invalid_state_error" && !account) {
        signinHandler();
        clearMfaValidated();
      }
    }

    const sessionItem = localStorage.getItem("msal.login.error");

    if (sessionItem?.includes("AADB2C90118")) {
      setErrorType(true);
      clearB2cAuthStoragePreservingDevice();
    }

    if (sessionItem?.includes("AADB2C90091")) {
      scheduleB2cSignInRedirect(instance);
    }

    if (sessionItem?.includes("AADB2C90077")) {
      const deviceId = localStorage.getItem("device_id");
      history.replace("/");
      instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
      localStorage.clear();
      if (deviceId) {
        localStorage.setItem("device_id", deviceId);
      }
    }

    const canBootstrapProfile =
      Helper.isLoggedIn() &&
      (isMfaValidate ||
        account?.idTokenClaims?.acr === "b2c_1a_trustframeworkbase_su" ||
        account?.idTokenClaims?.acr === "b2c_1a_passwordreset");

    if (!canBootstrapProfile) {
      if (!Helper.isLoggedIn() || !isMfaValidate) {
        profileBootstrapRef.current = false;
      }
      return;
    }

    // One profile/FCM bootstrap per MFA session. A second call toggles Redux
    // isLoading and remounts the post-login landing page (Offices, etc.).
    if (profileBootstrapRef.current) {
      return;
    }
    profileBootstrapRef.current = true;
    handleGetTokenManageState();
  }, [signinHandler, isMfaValidate, handleGetTokenManageState, account, instance, history]);

  useLayoutEffect(() => {
    const { pathname, search, hash } = location;
    const isLoggedIn = Helper.isLoggedIn();
    const searchParams = new URLSearchParams(search);
    const urlUserId = searchParams.get("userId");

    /**
     * Email links land on private detail routes. Public routes would replace
     * that URL with /404 before a passive effect could read it, so this runs
     * in layout and App hides those routes until login starts.
     * Logged-in users are left on the URL so the private route renders directly.
     */
    if (
      !isLoggedIn &&
      !readMfaValidatedRaw() &&
      !isLogoutInProgress() &&
      isProtectedDeepLink(pathname)
    ) {
      rememberPostLoginRedirect(pathname, search, hash);
      if (!hasAppRedirectedRef.current) {
        hasAppRedirectedRef.current = true;
        signinHandler();
      }
    }

    const profileId = profile?.id;
    const decodedUserId = urlUserId ? +decodeId(urlUserId) : null;
    if (profileId && decodedUserId && decodedUserId !== profileId) {
      const fullPath = pathname + search;
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, fullPath);
      sessionStorage.setItem(POST_LOGIN_USER_ID_KEY, urlUserId);
      history.replace("/");
      instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    }
  }, [location, profile, instance, signinHandler, history]);

  const handleSignup = useCallback(() => {
    signupHandler(location.pathname);
  }, [signupHandler, location.pathname]);

  /** MFA back / cancel: loader + env B2C SUSI sign-in (no public home). */
  const logoutFromMfa = useCallback(() => {
    redirectToB2cSignInFromMfa(instance);
  }, [instance]);

  return {
    instance,
    signinHandler,
    signupHandler,
    handleSignup,
    logout,
    logoutFromMfa,
    isLoginRedirectPending,
    cancelPendingLoginRedirect,
    isMfaValidate,
    setIsMfaValidate,
    isMfaChecking,
    /** False on `/` until home SendEmailMfa preflight finishes (avoids skipping `data: true`). */
    mfaHomePreflightDone,
    /** False until `msal.idtoken` is written (or silent acquisition failed and we unblock). */
    msalTokenReady: tokenAcquired,
    statusModal,
    closeModal,
    shouldShowTrialModal,
    errorType,
  };
};
